/**
 * The patient app and the staff panel are two products served from two
 * hostnames off **one** build:
 *
 *   booking.<domain>  → the patient app; `/admin` is not part of it
 *   admin.<domain>    → the panel, mounted at the root of its own host
 *
 * The hostname is read at runtime rather than baked in at build time, so a
 * single image serves both — the alternative is two builds and two deploys of
 * the same bundle, which drift.
 *
 * Anywhere the first label is neither (a bare IP, `localhost` in dev, jsdom in
 * tests) one origin serves everything and the panel keeps its `/admin` prefix,
 * exactly as before. That is what keeps `npm run dev` a single server.
 */
const ADMIN_LABEL = 'admin';
const PATIENT_LABEL = 'booking';

function firstLabel(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.location.hostname.split('.')[0];
}

/** True only on the panel's own hostname. */
export function isAdminHost(): boolean {
  return firstLabel() === ADMIN_LABEL;
}

/**
 * Prefix every panel address carries. Empty on the admin host — the panel owns
 * that root — and `/admin` everywhere else. Read once at module load: the
 * hostname cannot change without a full page load, which re-evaluates it.
 */
export const adminBasePath: string = isAdminHost() ? '' : '/admin';

/** `adminPath('/schedule')` → `/schedule` on the admin host, `/admin/schedule` elsewhere. */
export function adminPath(sub = ''): string {
  return `${adminBasePath}${sub}` || '/';
}

/**
 * Where a staff member signs in, linked **from the patient app**.
 *
 * On the patient hostname the panel is a different origin, so this has to be an
 * absolute URL — a path would land on a host that answers 302 for `/admin`.
 * Everywhere else the two share an origin and the in-app path is correct, which
 * keeps the link working in dev and in tests.
 */
export function staffLoginHref(): string {
  if (typeof window === 'undefined' || firstLabel() !== PATIENT_LABEL) {
    return adminPath('/login');
  }

  const { protocol, hostname, port } = window.location;
  const labels = hostname.split('.');

  labels[0] = ADMIN_LABEL;

  return `${protocol}//${labels.join('.')}${port === '' ? '' : `:${port}`}/login`;
}

/**
 * Where a رسید is read, linked **from the panel**.
 *
 * The mirror of `staffLoginHref`. The receipt page belongs to the patient app's
 * route table, so on the panel's own hostname a `/r/<code>` path would land on
 * a host that does not have that route at all. Everywhere else — dev, tests, a
 * bare IP — one origin serves both and the in-app path is the correct one.
 *
 * The code is percent-encoded rather than trusted: it arrives from the server,
 * but a URL built by concatenation is a habit that outlives its safe input.
 */
export function receiptHref(code: string): string {
  const path = `/r/${encodeURIComponent(code)}`;

  if (typeof window === 'undefined' || firstLabel() !== ADMIN_LABEL) {
    return path;
  }

  const { protocol, hostname, port } = window.location;
  const labels = hostname.split('.');

  labels[0] = PATIENT_LABEL;

  return `${protocol}//${labels.join('.')}${port === '' ? '' : `:${port}`}${path}`;
}

/**
 * Where the public site is served in development — Laravel's landing host
 * behind the dev nginx. Not configuration: a prod build never reaches it.
 */
const DEV_LANDING_ORIGIN = 'http://landing.localhost:8080';

/**
 * The public site — the bare domain the booking and admin hosts sit under
 * (`drkaramizadeh.ir`), served by Laravel rather than by this build.
 *
 * On one of the two named hostnames, dropping the first label gives the
 * domain. Anywhere else — a bare IP, `localhost`, jsdom — there is no domain to
 * derive, and the link goes to the dev landing host instead.
 */
export function landingHref(): string {
  const label = firstLabel();

  if (typeof window === 'undefined' || (label !== ADMIN_LABEL && label !== PATIENT_LABEL)) {
    return `${DEV_LANDING_ORIGIN}/`;
  }

  const { protocol, hostname, port } = window.location;
  const domain = hostname.split('.').slice(1).join('.');

  return `${protocol}//${domain}${port === '' ? '' : `:${port}`}/`;
}
